import React, { useMemo, useRef, useEffect, useState } from 'react';
import { AgCharts } from 'ag-charts-react';
import { getAgChartTheme, getChartColors } from '../../../analytics/charts/chartTheme';
import { useTheme } from '../../../../stores/ThemeContext';
import { Activity } from 'lucide-react';

const DisciplineOverTime = ({ data: payload, loading }) => {
  const { isDark } = useTheme();
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 300 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        if (width > 0) setDimensions({ width, height: 300 });
      }
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  const colors = useMemo(() => getChartColors(isDark), [isDark]);

  const { data = [], avg_score = 0 } = payload || {};

  const chartData = useMemo(() =>
    data.map((d) => ({
      ...d,
      dateLabel: d.trade_date
        ? new Date(d.trade_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : '—',
    })),
  [data]);

  const options = useMemo(() => ({
    theme: getAgChartTheme(isDark),
    width: dimensions.width || undefined,
    height: dimensions.height,
    data: chartData,
    series: [
      {
        type: 'line',
        xKey: 'dateLabel',
        yKey: 'discipline_score',
        yName: 'Discipline Score',
        stroke: colors.win,
        marker: {
          enabled: data.length <= 30,
          fill: colors.win,
          stroke: colors.win,
          size: 4,
        },
        strokeWidth: 1.5,
        tooltip: {
          renderer: ({ datum }) => ({
            title: datum.dateLabel,
            content: `Score: ${datum.discipline_score}%\nFollowed: ${datum.followed_count}/${datum.total_rules} rules\nPnL: $${datum.profit.toFixed(2)}`,
          }),
        },
      },
      {
        type: 'line',
        xKey: 'dateLabel',
        yKey: 'rolling_avg',
        yName: '7-Trade Rolling Avg',
        stroke: colors.primary,
        strokeWidth: 2,
        marker: { enabled: false },
        lineDash: [4, 3],
        tooltip: {
          renderer: ({ datum }) => ({
            title: datum.dateLabel,
            content: `7-trade avg: ${datum.rolling_avg}%`,
          }),
        },
      },
    ],
    axes: [
      {
        type: 'category',
        position: 'bottom',
        label: {
          color: isDark ? '#a1a1aa' : '#6b7280',
          fontSize: 10,
          rotation: data.length > 15 ? -45 : 0,
        },
      },
      {
        type: 'number',
        position: 'left',
        min: 0,
        max: 100,
        label: {
          color: isDark ? '#a1a1aa' : '#6b7280',
          formatter: ({ value }) => `${value}%`,
          fontSize: 10,
        },
      },
    ],
    legend: { enabled: true, position: 'top' },
  }), [chartData, dimensions, isDark, colors, data.length]);

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-4 animate-pulse">
        <div className="h-4 bg-muted rounded w-1/3 mb-4" />
        <div className="h-72 bg-muted rounded" />
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-4">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-muted-foreground">Discipline Score Over Time</h3>
        </div>
        {data.length > 0 && (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-muted-foreground">Avg:</span>
            <span className={`text-sm font-bold tabular-nums ${avg_score >= 80 ? 'text-green-400' : avg_score >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>
              {avg_score.toFixed(1)}%
            </span>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground/60 mb-3">% of active rules followed per trade</p>

      {data.length === 0 ? (
        <div className="h-72 flex flex-col items-center justify-center text-center">
          <Activity className="w-8 h-8 text-muted-foreground/30 mb-2" />
          <p className="text-sm text-muted-foreground">No journaled trades yet.</p>
        </div>
      ) : (
        <div ref={containerRef} className="w-full">
          {dimensions.width > 0 && <AgCharts options={options} />}
        </div>
      )}
    </div>
  );
};

export default DisciplineOverTime;
